import { generatePath } from 'react-router-dom';
import {
  ICampaignRouteParams,
  IInventoryRouteParams,
  IOneLinkRouteParams,
  IOrganizationRouteParams,
  IReportsRouteParams,
  ITemplateRouteParams,
  IUserRouteParams,
} from '../types/routing';

export enum SupportedFlowsEnum {
  Single = 'new',
  Bulk = 'bulk',
  OneLink = 'one-link',
  DirectSend = 'direct',
}

export const URL_VARS = {
  ROOT: '/',
  LOGIN: 'login',
  DASHBOARD: 'dashboard',
  ENGAGEMENT: 'send',
  CREATE_SEND: 'create-send',
  CONTENTS: 'contents',
  RECIPIENTS: 'recipients',
  REVIEW: 'review',
  LINK_SETTINGS: 'link-settings',
  SHIPMENT_INFO: 'shipment-info',
  CUSTOM: 'custom',
  INVENTORY: 'inventory',
  CAMPAIGNS: 'campaigns',
  AUTOMATED_CAMPAIGNS: 'automated-campaigns',
  REPORTS: 'reports',
  BOX: 'box',
  CUSTOMIZE: 'customize',
  UNAUTHORIZED: 'unauthorized',
  SHIPPING: 'shipping',
  NOTE: 'note',
  EMAIL: 'email',
  SUMMARY: 'summary',
  USERS: 'users',
  SETTINGS: 'settings',
  USER_INFO: 'user-info',
  UPLOAD: 'upload',
  INTEGRATIONS: 'integrations',
  SUGGEST_GIFT: 'suggest-gift',
  SF_SUCCESS: 'sf_success',
  HUBSPOT_SUCCESS: 'hubspot_success',
  OUTLOOK_SUCCESS: 'outlook_success',
  REPORT_ID: 'reportId',
  FLOW_ID: 'flowId',
  SINGLE: SupportedFlowsEnum.Single,
  BULK: SupportedFlowsEnum.Bulk,
  ONE_LINK: SupportedFlowsEnum.OneLink,
  DIRECT_SEND: SupportedFlowsEnum.DirectSend,
  ITEM_ID: 'itemId',
  STEP_ID: 'stepId',
  USER_ID: 'userId',
  ORG_ID: 'orgId',
  ADD: 'add',
  NEW: 'new',
  EDIT: 'edit',
  VIEW: 'view',
  DETAILS: 'details',
  TEXT_CUSTOMIZATION: 'text-customization',
  ITEMS: 'items',
  EXPERIENCE_MANAGER: 'experience-manager',
  EXPERIENCE_MANAGER_CLAIM_LINKS: 'claim-links',
  EXPERIENCE_MANAGER_EMAIL_TEMPLATES: 'email-templates',
  EXPERIENCE_MANAGER_CUSTOM_MESSAGES: 'custom-messages',
  CUSTOM_NOTE: 'custom-note',
  CLAIM_LINK: 'claim-page',
  CLAIM_PAGE_TEMPLATE: 'claim-page-template',
  ORGANIZATIONS: 'organizations',
  BUSINESS_REASONS: 'business-reasons',
  DIGITAL_BUDGET: 'digital-budget',
  ONE_LINK_MANAGER: 'one-link-manager',
  BULK_SEND_MANAGER: 'bulk-send-manager',
  TYPE: 'type',
  REGULAR: 'regular',
  SCHEDULED: 'scheduled',
  SIGNATURE_SETUP: 'signature-setup',
  STATUS: 'status',
  ACTIVE: 'active',
  NOT_ACTIVE: 'not_active',
  ADDRESS_BOOK: 'address-book',
  HUBSPOT_APP_INTEGRATION: 'hubspot-app',
  CHECK_EMAIL: 'check-email',
  LINK_EXPIRED: 'link-expired',
  VERIFY: 'verify',
};

export const routes = {
  root: `${URL_VARS.ROOT}`,
  login: `/${URL_VARS.LOGIN}`,
  checkEmail: `/${URL_VARS.LOGIN}/${URL_VARS.CHECK_EMAIL}`,
  linkExpired: `/${URL_VARS.LOGIN}/${URL_VARS.LINK_EXPIRED}`,
  verifyMagicLink: `/${URL_VARS.LOGIN}/${URL_VARS.VERIFY}`,
  dashboard: `/${URL_VARS.DASHBOARD}`,
  reports: {
    root: `/${URL_VARS.REPORTS}`,
    route: `/${URL_VARS.REPORTS}/:${URL_VARS.FLOW_ID}(${URL_VARS.SUMMARY})?/:${URL_VARS.REPORT_ID}?/:${URL_VARS.TYPE}(${URL_VARS.REGULAR}|${URL_VARS.SCHEDULED})`,
    getReportsUrl: (params: IReportsRouteParams) =>
      generatePath(routes.reports.route, params as { [key in keyof IReportsRouteParams]: string }),
  },
  createSend: {
    root: `/${URL_VARS.CREATE_SEND}`,
    route: `/${URL_VARS.CREATE_SEND}/:${URL_VARS.STEP_ID}?`,
    getUrl: (step: string) => `/${URL_VARS.CREATE_SEND}/${step}`,
  },
  users: {
    root: `/${URL_VARS.USERS}`,
    route: `/${URL_VARS.USERS}/:${URL_VARS.FLOW_ID}(${URL_VARS.NEW}|${URL_VARS.EDIT}|${URL_VARS.VIEW})?/:${URL_VARS.USER_ID}?`,
    getUserUrl: (params: IUserRouteParams) =>
      generatePath(routes.users.route, params as { [key in keyof IUserRouteParams]: string }),
  },
  organizations: {
    root: `/${URL_VARS.ORGANIZATIONS}`,
    route: `/${URL_VARS.ORGANIZATIONS}/:${URL_VARS.FLOW_ID}(${URL_VARS.NEW}|${URL_VARS.EDIT}|${URL_VARS.VIEW})?/:${URL_VARS.ORG_ID}?`,
    getOrganizationUrl: (params: IOrganizationRouteParams) =>
      generatePath(routes.organizations.route, params as { [key in keyof IOrganizationRouteParams]: string }),
  },
  businessReasons: `/${URL_VARS.BUSINESS_REASONS}`,
  addressBook: `/${URL_VARS.ADDRESS_BOOK}`,
  inventory: {
    root: `/${URL_VARS.INVENTORY}`,
    route: `/${URL_VARS.INVENTORY}/:${URL_VARS.FLOW_ID}(${URL_VARS.NEW}|${URL_VARS.EDIT}|${URL_VARS.VIEW})?/:${URL_VARS.ITEM_ID}?`,
    getInventoryUrl: (params: IInventoryRouteParams) =>
      generatePath(routes.inventory.route, params as { [key in keyof IInventoryRouteParams]: string }),
  },
  automations: `/${URL_VARS.AUTOMATED_CAMPAIGNS}`,
  campaigns: {
    root: `/${URL_VARS.CAMPAIGNS}`,
    route: `/${URL_VARS.CAMPAIGNS}/:${URL_VARS.ITEM_ID}?/:${URL_VARS.FLOW_ID}(${URL_VARS.NEW}|${URL_VARS.EDIT}|${URL_VARS.VIEW})/:${URL_VARS.STEP_ID}(${URL_VARS.DETAILS}|${URL_VARS.ITEMS}|${URL_VARS.SUMMARY}|${URL_VARS.TEXT_CUSTOMIZATION})?`,
    getCampaignUrl: (params: ICampaignRouteParams) =>
      generatePath(routes.campaigns.route, params as { [key in keyof ICampaignRouteParams]: string }),
  },
  templates: {
    root: `/${URL_VARS.EXPERIENCE_MANAGER}`,
    route: `/${URL_VARS.EXPERIENCE_MANAGER}/:${URL_VARS.TYPE}(${URL_VARS.EMAIL}|${URL_VARS.CUSTOM_NOTE}|${URL_VARS.CLAIM_LINK})?/:${URL_VARS.FLOW_ID}(${URL_VARS.NEW}|${URL_VARS.EDIT}|${URL_VARS.VIEW})?/:${URL_VARS.ITEM_ID}?`,
    getTemplateUrl: (params: ITemplateRouteParams) =>
      generatePath(routes.templates.route, params as { [key in keyof ITemplateRouteParams]: string }),
  },
  experienceManager: {
    root: `/${URL_VARS.EXPERIENCE_MANAGER}`,
    claimLink: `/${URL_VARS.EXPERIENCE_MANAGER}/${URL_VARS.EXPERIENCE_MANAGER_CLAIM_LINKS}`,
    claimLinkRoute: `/${URL_VARS.EXPERIENCE_MANAGER}/${URL_VARS.EXPERIENCE_MANAGER_CLAIM_LINKS}/:${URL_VARS.FLOW_ID}(${URL_VARS.NEW}|${URL_VARS.EDIT})?/:${URL_VARS.ITEM_ID}?`,
    emailTemplates: `/${URL_VARS.EXPERIENCE_MANAGER}/${URL_VARS.EXPERIENCE_MANAGER_EMAIL_TEMPLATES}`,
    emailTemplatesRoute: `/${URL_VARS.EXPERIENCE_MANAGER}/${URL_VARS.EXPERIENCE_MANAGER_EMAIL_TEMPLATES}/:${URL_VARS.FLOW_ID}(${URL_VARS.NEW}|${URL_VARS.EDIT})?/:${URL_VARS.ITEM_ID}?`,
    customMessages: `/${URL_VARS.EXPERIENCE_MANAGER}/${URL_VARS.EXPERIENCE_MANAGER_CUSTOM_MESSAGES}`,
    customMessagesRoute: `/${URL_VARS.EXPERIENCE_MANAGER}/${URL_VARS.EXPERIENCE_MANAGER_CUSTOM_MESSAGES}/:${URL_VARS.FLOW_ID}(${URL_VARS.NEW}|${URL_VARS.EDIT})?/:${URL_VARS.ITEM_ID}?`,
  },
  integrations: `/${URL_VARS.INTEGRATIONS}`,
  signatureSetup: `/${URL_VARS.SIGNATURE_SETUP}`,
  settings: `/${URL_VARS.SETTINGS}`,
  userInfo: `/${URL_VARS.USER_INFO}`,
  oneLinkManager: {
    root: `/${URL_VARS.ONE_LINK_MANAGER}`,
    route: `/${URL_VARS.ONE_LINK_MANAGER}/:${URL_VARS.ITEM_ID}/:${URL_VARS.STATUS}(${URL_VARS.ACTIVE}|${URL_VARS.NOT_ACTIVE})`,
    getOneLinkManagerUrl: (params: IOneLinkRouteParams) =>
      generatePath(routes.oneLinkManager.route, params as { [key in keyof IOneLinkRouteParams]: string }),
  },
  bulkSendManager: {
    root: `/${URL_VARS.BULK_SEND_MANAGER}`,
  },
  sfAuthorizeCallback: `/${URL_VARS.SF_SUCCESS}`,
  hubspotAuthorizeCallback: `/${URL_VARS.HUBSPOT_SUCCESS}`,
  outlookAuthorizeCallback: `/${URL_VARS.OUTLOOK_SUCCESS}`,
  suggestGift: `/${URL_VARS.SUGGEST_GIFT}`,
  hubspotAppIntegration: `/${URL_VARS.HUBSPOT_APP_INTEGRATION}`,
};

export const endpoints = {
  exchangeAuth: '/v1/integrations/code',
  checkIntegrations: '/v1/integrations/status',
  authorize: '/v1/auth/authorize',
  inventoryItemById: '/v1/inventory/{:id}',
  inventoryItems: '/v1/inventory',
  lowInventoryItems: '/v1/inventory/low',
  getCampaigns: '/v1/campaigns',
  marketplaceCatalog: '/v1/marketplace/catalog',
  marketplaceProductDetails: '/v1/marketplace/details/{:id}',
  marketplaceCategories: '/v1/marketplace/categories',
  notes: '/v1/templates/custom-message',
  emails: '/v1/templates/email',
  engagement: '/v1/sends',
  sendsV2: '/v1/sends/v2',
  bulkSendReport: '/v1/sends/v2/tasks/{:id}/report',
  bulkSendDownload: '/v1/sends/v2/tasks/{:id}/download',
  editEngagement: '/v1/sends/{:id}',
  editOneLink: '/v1/one-link/{:id}',
  getLeadsAndContacts: '/v1/integrations/crm/contacts',
  getOpportunities: '/v1/integrations/crm/relations',
  logs: '/v2_AddLogsInBulk',
  uploadFileToStorage: '/v1/shared/file-upload',
  bulkWithCSV: '/v2_CreateBulkEngagementsWithCSV',
  reports: '/v1/sends/reports/list',
  users: '/v1/users',
  addUser: '/v1/users',
  editUser: '/v1/users/{:id}',
  editUserProfile: '/v1/me/profile',
  toggleDigitalBalanceEmails: '/v1/me/digital-balance-emails',
  deleteUser: '/v1/users/{:id}',
  addAutomationConnection: '/v1/automation/connection',
  updateAutomationConnection: '/v1/automation/connection/{:id}',
  deleteAutomationConnection: '/v1/automation/connection/{:id}',
  testAutomationConnection: '/v1/automation/connection/{:id}/test',
  report: '/v1/sends/reports/{:id}',
  addInventoryItem: '/v1/inventory/create',
  editInventoryItem: '/v1/inventory/{:id}',
  getInventoryImagesList: '/v1/inventory/images',
  deleteInventoryItem: '/v1/inventory/{:id}',
  addCampaign: '/v1/campaigns',
  editCampaign: '/v1/campaigns/{:id}',
  deleteCampaign: '/v1/campaigns/{:id}',
  getOrganizations: '/v1/organizations',
  changeOrganization: '/v1/auth/organization/change',
  getOrgCustomizationOptions: '/v1/organizations/customization',
  getCampaignIconsList: '/v1/campaigns/icons',
  getBusinessReasons: '/v1/business-reasons',
  addBusinessReason: '/v1/business-reasons',
  deleteBusinessReason: '/v1/business-reasons/{:id}',
  editBusinessReason: '/v1/business-reasons/{:id}',
  createTemplate: '/v1/templates',
  deleteTemplate: '/v1/templates/{:id}',
  editTemplate: '/v1/templates',
  getAccountBalance: '/v1/auth/digital-balance',
  generateAPIKey: '/v1/integrations/api-key',
  getUserApiKey: '/v1/integrations/api-key',
  createOrgDepartment: '/v1/organizations/departments',
  editOrgDepartment: '/v1/organizations/departments',
  getBluebirdMetadata: '/v1/shared/metadata',
  createOrg: '/v1/organizations',
  editOrg: '/v1/organizations/{:id}',
  validateOrderKey: '/v1/organizations/validate-order-key',
  deleteEngagement: '/v1/sends/{:id}',
  cancelEngagement: '/v1/sends/{:id}/cancel',
  getCampaignSummaries: '/v1/campaigns/summaries',
  fulfillmentCenters: '/v1/fulfillment-centers',
  saveEmailSignature: '/v1/me/email-signature',
  downloadReportsCSV: '/v1/sends/report/csv',
  getDashboard: '/v1/dashboard',
  getDepartmentBudgetSummaries: '/v1/organizations/departments/budgets',
  updateDepartmentBudget: '/v2_UpdateDepartmentBudget',
  createOneLink: '/v1/one-link',
  getOneLinkSends: '/v1/one-link/list',
  getOneLinkSendById: '/v1/one-link/{:id}',
  disableOneLinkSend: '/v1/one-link/{:id}/deactivate',
  activateOneLinkSend: '/v1/one-link/{:id}/activate',
  downloadInventoryReportCSV: '/v1/inventory/report',
  handlePIIForOrganization: '/v1/organizations/pii',
  sendFeedback: '/v2_SaveUserFeedback',
  improveText: '/v1/shared/ai-improve',
  bulkCreateInventoryItemsFromCSV: '/v1/inventory/bulk/csv',
  generateGiftSuggest: '/v2_GenerateGiftSuggestions',
  editSendField: '/v1/sends/{:id}',
  deleteOneLinkRecipient: '/v1/one-link/recipients/{:id}',
  updateOneLinkRecipient: '/v1/one-link/recipients/{:id}',
  createOneLinkRecipients: '/v1/one-link/recipients',
  getOneLinkRecipients: '/v1/one-link/recipients',
  getAddressBook: '/v1/organizations/address-book',
  createOrgAddress: '/v1/organizations/address',
  createBulkOrgAddresses: '/v1/organizations/address/bulk',
  updateOrgAddress: '/v1/organizations/address',
  addAutomatedCampaign: '/v1/automation/campaigns',
  updateAutomationCampaign: '/v1/automation/campaigns/{:id}',
  deleteAutomatedCampaign: '/v1/automation/campaigns/{:id}',
  automatedCampaigns: '/v1/automation/campaigns',
  downloadCampaignReportCSV: '/v1/campaigns/report',
  installHubspotApp: '/v1/integrations/hubspot-app/install',
  getClaimLinkTemplate: '/v1/experience/{:id}',
  getClaimLinkTemplates: '/v1/experience/list',
  addClaimLinkTemplate: '/v1/experience',
  editClaimLinkTemplate: '/v1/experience/{:id}',
  updateClaimLinkTemplateDetails: '/v1/experience/{:id}',
  getClaimLinkTemplateById: '/v1/experience/{:id}',
  sendMagicLink: '/v1/auth/magic-link/send',
};

export const queryParams = {
  deptSearch: 'dept_search',
  orgSearch: 'org_search',
  uid: 'uid',
  cid: 'cid',
  orgId: 'org_id',
  itemId: 'item_id',
  responseData: 'response_data',
  orderKey: 'order_key',
  orderId: 'order_id',
  type: 'type',
  searchType: 'search_type',
  engagementType: 'engagement_type',
  campaignId: 'campaign_id',
  dashboardSubOption: 'sub',
  fileType: 'file_type',
  orgInventory: 'org_inventory',
  oneLinkId: 'one_link_id',
  all: 'all',
  newOwnerId: 'new_owner_id',
};

export const crmEndpoints = {
  authorizeSF: '/services/oauth2/authorize',
  authorizeHubspot: '/oauth/authorize',
  authorizeOutlook: '/oauth2/v2.0/authorize',
};

export const IE_PP_URL = 'https://www.imprintengine.com/privacy-policy';
export const IE_HC_URL = 'https://help.imprintengine.com/knowledge';
